import { useEffect, useMemo, useRef, useState, type ImgHTMLAttributes } from 'react';
import { apiUrl, authHeaders } from '@/lib/shell';
import { useSessionBoundFetch } from '@/lib/use-session-guard';

/** An issued Artifact variant needs the same tab identity as its saved view. */
export function ManagedImage({ src, ...props }: ImgHTMLAttributes<HTMLImageElement>) {
  const fetchImage = useSessionBoundFetch();
  const request = useMemo(() => ({ src, fetchImage }), [src, fetchImage]);
  const image = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState<{
    request: typeof request;
    url: string;
  } | null>(null);
  const managed =
    typeof src === 'string' &&
    /^\/api\/v1\/company\/artifacts\/versions\/[^/?#]+\/images\/[^/?#]+\/(?:master|320|640|1280)\?/.test(
      src,
    );

  useEffect(() => {
    const { src, fetchImage } = request;
    if (!managed || !src) return;
    const abort = new AbortController();
    let url: string | null = null;
    void (async () => {
      try {
        const response = await fetchImage(apiUrl(src), {
          credentials: 'same-origin',
          headers: authHeaders(),
          signal: abort.signal,
        });
        if (!response.ok) throw new Error('managed_image_unavailable');
        const blob = await response.blob();
        if (abort.signal.aborted) return;
        url = URL.createObjectURL(blob);
        setLoaded({ request, url });
      } catch {
        if (!abort.signal.aborted) {
          const node = image.current;
          const EventClass = node?.ownerDocument.defaultView?.Event;
          if (node && EventClass) node.dispatchEvent(new EventClass('error'));
        }
      }
    })();
    return () => {
      abort.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [request, managed]);

  const pixels = loaded?.request === request ? loaded.url : undefined;
  return <img {...props} ref={image} src={managed ? pixels : src} />;
}
